import type { UseMutationOptions } from '@tanstack/react-query'
import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import type { QueryError } from 'src/queries/network.utils'
import type {
  CardManagementStatusChangeResponse,
  CardManagementStatusResponse,
  CreateCardManagementStatusChangeInput,
  CreatePermanentLimitChangeInput,
  LimitChangeResponse,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import {
  createPermanentLimitChange,
  fetchCardManagementCapabilities,
  fetchCardManagementLimits,
  fetchCardManagementStatus,
  freezePhysicalCard,
  unfreezePhysicalCard,
} from 'src/queries/physicalCardManagement/physicalCardManagement.network'
import type { CustomMutationOptions } from 'src/queries/queries.utils'

export const physicalCardManagementQueryKeys = {
  capabilitiesFamily: () => ['physicalCardManagement', 'capabilities'] as const,
  capabilities: (creditCardId: string | undefined) =>
    [
      ...physicalCardManagementQueryKeys.capabilitiesFamily(),
      creditCardId,
    ] as const,
  limitsFamily: () => ['physicalCardManagement', 'limits'] as const,
  limits: (creditCardId: string | undefined) =>
    [...physicalCardManagementQueryKeys.limitsFamily(), creditCardId] as const,
  statusFamily: () => ['physicalCardManagement', 'status'] as const,
  status: (creditCardId: string | undefined) =>
    [...physicalCardManagementQueryKeys.statusFamily(), creditCardId] as const,
}

const CAPABILITIES_STALE_TIME_MS = 30_000

const requireCreditCardId = (
  creditCardId: string | undefined,
  resource: string,
): string => {
  if (!creditCardId) {
    throw new Error(`Card Management ${resource} require a creditCardId`)
  }

  return creditCardId
}

export const physicalCardManagementQueries = {
  capabilities: (creditCardId: string | undefined) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.capabilities(creditCardId),
      queryFn: () =>
        fetchCardManagementCapabilities(
          requireCreditCardId(creditCardId, 'capabilities'),
        ),
      enabled: Boolean(creditCardId),
      retry: false,
      staleTime: CAPABILITIES_STALE_TIME_MS,
    }),
  limits: (creditCardId: string | undefined) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.limits(creditCardId),
      queryFn: () =>
        fetchCardManagementLimits(requireCreditCardId(creditCardId, 'limits')),
      enabled: Boolean(creditCardId),
    }),
  status: (creditCardId: string | undefined) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.status(creditCardId),
      queryFn: () =>
        fetchCardManagementStatus(
          requireCreditCardId(creditCardId, 'status reads'),
        ),
      enabled: Boolean(creditCardId),
    }),
}

export const physicalCardManagementMutations = {
  createPermanentLimitChange: () =>
    mutationOptions<
      LimitChangeResponse,
      QueryError,
      CreatePermanentLimitChangeInput
    >({
      mutationFn: createPermanentLimitChange,
    }),
  freezePhysicalCard: () =>
    mutationOptions<
      CardManagementStatusChangeResponse,
      QueryError,
      CreateCardManagementStatusChangeInput
    >({
      mutationFn: freezePhysicalCard,
    }),
  unfreezePhysicalCard: () =>
    mutationOptions<
      CardManagementStatusChangeResponse,
      QueryError,
      CreateCardManagementStatusChangeInput
    >({
      mutationFn: unfreezePhysicalCard,
    }),
}

type StatusChangeMutationOptions = CustomMutationOptions<
  CardManagementStatusChangeResponse,
  QueryError,
  CreateCardManagementStatusChangeInput
>

const useStatusChange = (
  mutationOptionsFactory: () => UseMutationOptions<
    CardManagementStatusChangeResponse,
    QueryError,
    CreateCardManagementStatusChangeInput
  >,
  options?: StatusChangeMutationOptions,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...mutationOptionsFactory(),
    onSuccess: (data, variables, onMutateResult, context) => {
      const statusKey = physicalCardManagementQueryKeys.status(
        variables.creditCardId,
      )
      queryClient.setQueryData<CardManagementStatusResponse>(
        statusKey,
        (current) =>
          current && current.version > data.card.version ? current : data.card,
      )
      if (data.status === 'PENDING') {
        // The contract-only client performs the required immediate refresh.
        // Longer-lived convergence polling awaits EX-40587's runtime timing
        // and lifecycle contract and belongs with the consuming UI slice.
        void queryClient.invalidateQueries({ queryKey: statusKey, exact: true })
      }
      onSuccess?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

export const useFreezePhysicalCard = (options?: StatusChangeMutationOptions) =>
  useStatusChange(physicalCardManagementMutations.freezePhysicalCard, options)

export const useUnfreezePhysicalCard = (
  options?: StatusChangeMutationOptions,
) =>
  useStatusChange(physicalCardManagementMutations.unfreezePhysicalCard, options)

export const useCreatePermanentLimitChange = (
  options?: CustomMutationOptions<
    LimitChangeResponse,
    QueryError,
    CreatePermanentLimitChangeInput
  >,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...physicalCardManagementMutations.createPermanentLimitChange(),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData(
        physicalCardManagementQueryKeys.limits(variables.creditCardId),
        data.limits,
      )
      onSuccess?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}
